/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { toast } from "react-hot-toast";
import { BsDownload, BsPencil, BsTrash, BsPersonPlus } from "react-icons/bs";
import { useNavigate } from "react-router-dom";

import { useAuthContext } from "../../context/auth";
import { calculateImpactosEfectivos } from "../../utils";
import {
  getMisCotizaciones,
  eliminarCotizacion,
  actualizarCotizacion,
  getClientesPorEjecutivo,
} from "../../utils/cotizacionesGuardadas";
import { Header } from "../Header";
import PdfCotizacion from "../PdfCotizacion";
import { pdf } from "@react-pdf/renderer";
import styles from "./MisCotizaciones.module.scss";

const MisCotizaciones = () => {
  const { pais, id: userId, rol, agencia } = useAuthContext();
  const navigate = useNavigate();

  // =====================================================
  // STATE
  // =====================================================
  const [cotizaciones, setCotizaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [sortColumn, setSortColumn] = useState("created_at");
  const [sortAsc, setSortAsc] = useState(false);

  // Modal asociar cliente
  const [showClienteModal, setShowClienteModal] = useState(null);
  const [clientesAgencia, setClientesAgencia] = useState([]);
  const [clientesLoading, setClientesLoading] = useState(false);
  const [selectedClienteId, setSelectedClienteId] = useState("");

  // Modal confirmar eliminación
  const [confirmDelete, setConfirmDelete] = useState(null);

  // =====================================================
  // FORMATO MONEDA
  // =====================================================
  const formatNum = useCallback(
    (amount) => {
      const num = Number(amount) || 0;
      const locale = pais === "Chile" ? "es-CL" : "es-PE";
      return num.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    [pais]
  );

  const monedaSymbol = pais === "Chile" ? "$" : "S/";

  // =====================================================
  // FETCH DATA
  // =====================================================
  const fetchCotizaciones = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMisCotizaciones(pais, userId, rol, agencia);
      setCotizaciones(data);
    } catch (err) {
      console.error("Error cargando cotizaciones:", err);
    } finally {
      setLoading(false);
    }
  }, [pais, userId, rol, agencia]);

  useEffect(() => {
    if (pais && userId) fetchCotizaciones();
  }, [pais, userId, fetchCotizaciones]);

  // Cargar clientes vinculados al ejecutivo de agencia
  useEffect(() => {
    if (rol !== "agencia" || !userId || !pais) return;
    const fetchClientes = async () => {
      setClientesLoading(true);
      try {
        const data = await getClientesPorEjecutivo(pais, userId);
        setClientesAgencia(data);
      } catch {
        setClientesAgencia([]);
      } finally {
        setClientesLoading(false);
      }
    };
    fetchClientes();
  }, [rol, userId, pais]);

  // =====================================================
  // FILTRADO Y ORDENAMIENTO
  // =====================================================
  const cotizacionesFiltradas = useMemo(() => {
    let filtered = [...cotizaciones];

    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          (c.codigo_generado || "").toLowerCase().includes(q) ||
          (c.nombre_cotizacion || "").toLowerCase().includes(q) ||
          (c.cliente_nombre || "").toLowerCase().includes(q)
      );
    }

    if (filtroEstado) {
      filtered = filtered.filter((c) => c.estado === filtroEstado);
    }

    // Agrupar por version_grupo y ordenar: grupo más reciente primero, dentro del grupo v1 primero (más nueva última)
    const grupoMap = {};
    for (const c of filtered) {
      const grp = c.version_grupo || c.codigo_generado;
      if (!grupoMap[grp]) grupoMap[grp] = [];
      grupoMap[grp].push(c);
    }

    // Ordenar cada grupo internamente: versión más nueva primero
    const grupos = Object.values(grupoMap).map((items) => {
      items.sort((a, b) => (b.version || 1) - (a.version || 1));
      return items;
    });

    // Ordenar grupos por fecha más reciente del grupo
    grupos.sort((a, b) => {
      const dateA = a[0].created_at ? new Date(a[0].created_at).getTime() : 0;
      const dateB = b[0].created_at ? new Date(b[0].created_at).getTime() : 0;
      return dateB - dateA;
    });

    // Flatten y marcar filas hijas
    const result = [];
    for (const items of grupos) {
      const isGroup = items.length > 1;
      items.forEach((c, idx) => {
        result.push({ ...c, _isGroup: isGroup, _isChild: isGroup && idx > 0 });
      });
    }

    return result;
  }, [cotizaciones, busqueda, filtroEstado]);

  // =====================================================
  // HANDLERS
  // =====================================================
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortAsc(!sortAsc);
    } else {
      setSortColumn(column);
      setSortAsc(true);
    }
  };

  const sortIndicator = (column) => {
    if (sortColumn !== column) return "";
    return sortAsc ? " \u25B2" : " \u25BC";
  };

  const handleDelete = async (cotizacion) => {
    try {
      await eliminarCotizacion(cotizacion.id);
      toast.success("Cotización eliminada");
      setConfirmDelete(null);
      fetchCotizaciones();
    } catch (err) {
      toast.error(err.message || "Error al eliminar");
    }
  };

  const handleEdit = (cotizacion) => {
    navigate("/admin/online-cotizador", { state: { cotizacionGuardada: cotizacion } });
  };

  const handleDownloadPdf = async (c) => {
    try {
      toast.loading("Generando PDF...", { id: "pdf-gen" });
      const moneda = c.moneda_dolar ? "US$" : pais === "Chile" ? "$" : "S/";
      const impactosEfectivos = calculateImpactosEfectivos({
        impactosTotalAscensores: c.impactos_total_ascensores || 0,
        impactosTotalTotems: c.impactos_total_totems || 0,
        impactosTotalCowork: 0,
        impactosTotalEspera: 0,
        impactosTotales: c.impactos_totales || 0,
      });
      const doc = (
        <PdfCotizacion
          pais={c.pais || pais}
          cliente={c.cliente_nombre || ""}
          campana={c.nombre_cotizacion || ""}
          codigoGenerado={c.codigo_generado}
          usuarioNombre={c.usuario_nombre || ""}
          usuarioApellido={c.usuario_apellido || ""}
          selectedMoneda={moneda}
          isProgrammaticSelected={c.is_programmatic_selected || false}
          includeInstallingBuildings={false}
          plazo={c.plazo || 0}
          frecuencia={c.frecuencia || 0}
          duracion={c.duracion || 0}
          horasFinal={c.horas_final || c.horas || 0}
          intervalos={c.intervalos || []}
          options={c.options || []}
          ascensoresSeleccionados={c.ascensores_seleccionados || 0}
          totemsSeleccionados={c.totems_seleccionados || 0}
          coworkSeleccionados={0}
          esperaSeleccionados={0}
          audienciaSeleccionadaTotal={c.audiencia_seleccionada_total || 0}
          audienciaSeleccionadaAscensor={c.audiencia_seleccionada_ascensor || 0}
          audienciaSeleccionadaTotems={c.audiencia_seleccionada_totems || 0}
          audienciaSeleccionadaCowork={0}
          audienciaSeleccionadaEspera={0}
          salidasAscensores={c.salidas_ascensores || 0}
          salidasTotems={c.salidas_totems || 0}
          salidasCowork={0}
          salidasEspera={0}
          salidasTotales={c.salidas_totales || 0}
          impactosTotalAscensores={c.impactos_total_ascensores || 0}
          impactosTotalTotems={c.impactos_total_totems || 0}
          impactosTotalCowork={0}
          impactosTotalEspera={0}
          impactosTotales={c.impactos_totales || 0}
          impactosEfectivosPoisson={impactosEfectivos}
          inversionAscensoresFinal={c.inversion_ascensores_final || 0}
          inversionTotemsFinal={c.inversion_totems_final || 0}
          inversionCoworkFinal={0}
          inversionEsperaFinal={0}
          inversionFinalTotal={c.inversion_final_total || 0}
          tarifaFriaAscensores={c.tarifa_fria_ascensores || 0}
          tarifaFriaTotems={c.tarifa_fria_totems || 0}
          tarifaFriaCowork={0}
          tarifaFriaEspera={0}
          tarifaFriaTotal={c.tarifa_fria_total || 0}
          cpmAscensoresFinal={c.cpm_ascensores_final || 0}
          cpmTotemsFinal={c.cpm_totems_final || 0}
          cpmCoworkFinal={0}
          cpmEsperaFinal={0}
          cpmTotalFinal={c.cpm_total_final || 0}
          descuentoGlobal={c.descuento_total_final || 0}
          manualDiscountAmount={c.manual_discount_amount || null}
          manualDiscountPercentage={c.discount_percentage || null}
          finalAmount={c.final_amount || null}
          nombresEdificiosSeleccionadosAscensores={c.nombres_edificios_seleccionados_ascensores || []}
          nombresEdificiosSeleccionadosTotems={c.nombres_edificios_seleccionados_totems || []}
          nombresEdificiosSeleccionadosCowork={[]}
          nombresEdificiosSeleccionadosEspera={[]}
          nombresEdificiosNoSeleccionados={[]}
          edificios={[]}
        />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Cotizacion_${c.codigo_generado}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("PDF descargado", { id: "pdf-gen" });
    } catch (err) {
      console.error("Error generando PDF:", err);
      toast.error("Error al generar PDF", { id: "pdf-gen" });
    }
  };

  const handleAsociarCliente = async () => {
    if (!selectedClienteId || !showClienteModal) return;
    const cliente = clientesAgencia.find((c) => String(c.id) === String(selectedClienteId));
    if (!cliente) return;

    try {
      await actualizarCotizacion(showClienteModal.id, { cliente_id: cliente.id, cliente_nombre: cliente.nombre });
      toast.success(`Cliente "${cliente.nombre}" asociado`);
      setShowClienteModal(null);
      setSelectedClienteId("");
      fetchCotizaciones();
    } catch {
      toast.error("Error al asociar cliente");
    }
  };

  const formatFecha = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  // =====================================================
  // RENDER
  // =====================================================
  if (loading) {
    return (
      <div className={styles.wrapper}>
        <Header leftContent={<h2 className={styles.titulo}>Mis Cotizaciones</h2>} />
        <div className={styles.content}>
          <div className={styles.empty}>Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Header leftContent={<h2 className={styles.titulo}>Mis Cotizaciones</h2>} />
      <div className={styles.content}>
        {/* ─── FILTROS ─── */}
        <div className={styles.filterBar}>
          <input
            type="text"
            className="form-control"
            placeholder="Buscar por código, nombre o cliente..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          <select className="form-select" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
            <option value="">Todos los estados</option>
            <option value="borrador">Borrador</option>
            <option value="ejecutada">Ejecutada</option>
          </select>
        </div>

        {/* ─── TABLA ─── */}
        {cotizacionesFiltradas.length === 0 ? (
          <div className={styles.empty}>
            {cotizaciones.length === 0
              ? "No tienes cotizaciones guardadas. Crea una desde el Cotizador."
              : "No se encontraron cotizaciones con los filtros aplicados."}
          </div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.cotizacionesTable}>
              <thead>
                <tr>
                  <th onClick={() => handleSort("created_at")}>Fecha{sortIndicator("created_at")}</th>
                  <th onClick={() => handleSort("codigo_generado")}>Código{sortIndicator("codigo_generado")}</th>
                  <th onClick={() => handleSort("estado")}>Estado{sortIndicator("estado")}</th>
                  <th onClick={() => handleSort("cliente_nombre")}>Cliente{sortIndicator("cliente_nombre")}</th>
                  <th onClick={() => handleSort("nombre_cotizacion")}>
                    Descripción{sortIndicator("nombre_cotizacion")}
                  </th>
                  <th onClick={() => handleSort("edificios_unicos_total")}>
                    Edificios{sortIndicator("edificios_unicos_total")}
                  </th>
                  <th onClick={() => handleSort("ascensores_seleccionados")}>
                    Pantallas{sortIndicator("ascensores_seleccionados")}
                  </th>
                  <th onClick={() => handleSort("inversion_final_total")}>
                    Inversión{sortIndicator("inversion_final_total")}
                  </th>
                  <th onClick={() => handleSort("confianza_agencia")}>Confianza{sortIndicator("confianza_agencia")}</th>
                  <th onClick={() => handleSort("version")}>Versión{sortIndicator("version")}</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cotizacionesFiltradas.map((c) => (
                  <tr
                    key={c.id}
                    className={c._isChild ? styles.versionChildRow : c._isGroup ? styles.versionGroupRow : ""}
                  >
                    <td style={{ textAlign: "center" }}>{formatFecha(c.created_at)}</td>
                    <td style={{ textAlign: "center", fontWeight: 600 }}>{c.codigo_generado}</td>
                    <td style={{ textAlign: "center" }}>
                      <span className={`${styles.estadoBadge} ${styles[c.estado || "borrador"]}`}>
                        {c.estado || "borrador"}
                      </span>
                    </td>
                    <td>{c.cliente_nombre || <span style={{ color: "#999" }}>Sin asignar</span>}</td>
                    <td>{c.nombre_cotizacion || "-"}</td>
                    <td style={{ textAlign: "center" }}>{c.edificios_unicos_total || "-"}</td>
                    <td style={{ textAlign: "center" }}>
                      {(c.ascensores_seleccionados || 0) + (c.totems_seleccionados || 0) || "-"}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {c.moneda_dolar
                        ? `USD ${formatNum(c.final_amount || c.inversion_final_total)}`
                        : `${monedaSymbol} ${formatNum(c.final_amount || c.inversion_final_total)}`}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <select
                        className={`${styles.confianzaSelect} ${
                          styles[`confianza_${(c.confianza_agencia || "").replace("%", "")}`] || ""
                        }`}
                        value={c.confianza_agencia || ""}
                        onChange={async (e) => {
                          const val = e.target.value || null;
                          try {
                            await actualizarCotizacion(c.id, { confianza_agencia: val });
                            setCotizaciones((prev) =>
                              prev.map((cot) => (cot.id === c.id ? { ...cot, confianza_agencia: val } : cot))
                            );
                            toast.success("Confianza actualizada");
                          } catch {
                            toast.error("Error al actualizar confianza");
                          }
                        }}
                      >
                        <option value="">-</option>
                        <option value="25%">25%</option>
                        <option value="50%">50%</option>
                        <option value="75%">75%</option>
                        <option value="99%">99%</option>
                      </select>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span className={styles.versionBadge}>{c.version || 1}</span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        className={`${styles.actionBtn} ${styles.pdfBtn}`}
                        title="Descargar PDF"
                        onClick={() => handleDownloadPdf(c)}
                      >
                        <BsDownload />
                      </button>
                      <button
                        className={`${styles.actionBtn} ${styles.editBtn}`}
                        title="Editar cotización"
                        onClick={() => handleEdit(c)}
                      >
                        <BsPencil />
                      </button>
                      {rol === "agencia" && (
                        <button
                          className={`${styles.actionBtn} ${styles.clienteBtn}`}
                          title="Asociar cliente"
                          onClick={() => {
                            setShowClienteModal(c);
                            setSelectedClienteId(c.cliente_id ? String(c.cliente_id) : "");
                          }}
                        >
                          <BsPersonPlus />
                        </button>
                      )}
                      <button
                        className={`${styles.actionBtn} ${styles.deleteBtn}`}
                        title={
                          c.estado === "ejecutada"
                            ? "Cotización ejecutada, no se puede eliminar"
                            : "Eliminar cotización"
                        }
                        disabled={c.estado === "ejecutada"}
                        onClick={() => setConfirmDelete(c)}
                      >
                        <BsTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── MODAL CONFIRMAR ELIMINACIÓN ─── */}
      {confirmDelete && (
        <div className={styles.modalOverlay} onClick={() => setConfirmDelete(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>Eliminar cotización</h5>
              <button className="btn-close" onClick={() => setConfirmDelete(null)} />
            </div>
            <div className={styles.modalBody}>
              <p className={styles.confirmText}>
                ¿Estás seguro de eliminar la cotización <strong>{confirmDelete.codigo_generado}</strong>
                {confirmDelete.nombre_cotizacion ? ` - "${confirmDelete.nombre_cotizacion}"` : ""}?
              </p>
              <div className={styles.confirmWarning}>Esta acción no se puede deshacer.</div>
            </div>
            <div className={styles.modalFooter}>
              <button className="btn btn-sm btn-secondary" onClick={() => setConfirmDelete(null)}>
                Cancelar
              </button>
              <button className="btn btn-sm btn-danger" onClick={() => handleDelete(confirmDelete)}>
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL ASOCIAR CLIENTE (AGENCIA) ─── */}
      {showClienteModal && (
        <div className={styles.modalOverlay} onClick={() => setShowClienteModal(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>Asociar cliente</h5>
              <button className="btn-close" onClick={() => setShowClienteModal(null)} />
            </div>
            <div className={styles.modalBody}>
              {clientesLoading ? (
                <p>Cargando clientes...</p>
              ) : clientesAgencia.length === 0 ? (
                <div className={styles.emptyClientes}>
                  No tiene clientes asociados, contactar a su ejecutivo comercial.
                </div>
              ) : (
                <div className={styles.formGroup}>
                  <label>Seleccionar cliente</label>
                  <select
                    className="form-select"
                    value={selectedClienteId}
                    onChange={(e) => setSelectedClienteId(e.target.value)}
                  >
                    <option value="">-- Seleccionar --</option>
                    {clientesAgencia.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nombre}
                        {c.producto ? ` - ${c.producto}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className="btn btn-sm btn-secondary" onClick={() => setShowClienteModal(null)}>
                Cancelar
              </button>
              {clientesAgencia.length > 0 && (
                <button className="btn btn-sm btn-dark" disabled={!selectedClienteId} onClick={handleAsociarCliente}>
                  Asociar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MisCotizaciones;
